import { Injectable } from "@angular/core";
import { firstValueFrom, map, mergeMap, of, Subject, toArray } from "rxjs";
import { ActiveUserProfilePromiseService, SystemConfigPromiseService } from "shared/auth-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OrderFillResultImpl } from "shared/cubiq-model-multi-asset";
import { TRADING_SOURCE_INTERNAL } from "shared/cubiq-services/model/TradingSource.constants";
import {
  PRIMITIVE,
  ServiceMessageBroker,
  ServiceMessageBrokerFactory,
} from "shared/provider-webworker";
import { CcyPairLookupService } from "../../lookup/model/impl/CcyPairLookupService";
import { PositionBookLookupService } from "../../lookup/model/impl/PositionBookLookupService";
import { NotificationService } from "../../lookup/notification/impl/NotificationService";
import { DetailFillOrderAdapter } from "../../mediator/adapter/DetailFillOrderAdapter";
import { FillOrderData } from "../../model/core/FillOrderData";
import { SysFeatures } from "../../model/core/SysFeatures";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { CHANNEL_ORDER_DATA_ACTION_SERVICE } from "../IOrderDataActionService";
import { DetailedOrderStatusRequestPrimaryService } from "./DetailedOrderStatusRequestPrimaryService";
import { OrderDataActionPromiseService } from "./OrderDataActionPromiseService";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ManualNettingAction } from "shared/cubiq-model-multi-asset/model/enum/ManualNettingAction";
import { BenchmarkDataLookupService } from "shared/cubiq-services/lookup/model/impl/BenchmarkDataLookupService";
import { OrderDataLookupService } from "shared/cubiq-services/lookup/model/impl/OrderDataLookupService";
import { ScratchPadDataLookupService } from "shared/cubiq-services/lookup/model/impl/ScratchPadDataLookupService";
import { ActiveUser } from "shared/cubiq-services/model/core/ActiveUser";
import { BFXNotificationEventType } from "shared/cubiq-services/model/core/notifications/BFXNotificationEventType";
import { INotificationEvent } from "shared/cubiq-services/model/core/notifications/INotificationEvent";
import { BFXNotificationSubType } from "../../model/core/notifications/BFXNotificationSubType";
import { BFXNotificationType } from "../../model/core/notifications/BFXNotificationType";
import { RateCache } from "../../rate/impl/RateCache";
import { ManualNettingRequest } from "shared/cubiq-model-multi-asset/model/ManualNettingRequest";
import { ManualNettingRequestImpl } from "shared/cubiq-model-multi-asset/model/impl/ManualNettingRequestImpl";
import { ManualNettingResponseImpl } from "shared/cubiq-model-multi-asset/model/impl/ManualNettingResponseImpl";
import { DetailFillCcyPair } from "./DetailFillCcyPair";

@Injectable()
export class OrderDataActionPrimaryService extends OrderDataActionPromiseService {
  protected broker: ServiceMessageBroker;
  protected NEW_ORDER_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_ORDER_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_INFO_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_RESPONSE: CubiqTS.ConcreteEncodable;
  protected AMEND_MARKUP_LOCK_RESPONSE: CubiqTS.ConcreteEncodable;
  protected AMEND_MARKUP_APPROVAL_REQUEST: CubiqTS.ConcreteEncodable;

  protected PAUSE_RESUME_REQUEST: CubiqTS.ConcreteEncodable;
  protected PAUSE_RESUME_RESPONSE: CubiqTS.ConcreteEncodable;

  protected NEW_BMARK_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_BMARK_REQUEST: CubiqTS.ConcreteEncodable;

  private detailFillBatchCount: number = 50;
  private cancelBatchCount: number = 50;
  private maxCancelBurstCount: number = 1;
  // protected modelUpdateEventPorts: Array<MessagePort> = new Array<MessagePort>();
  // protected modelNotificationFailureEventSubject: Subject<any> = new Subject<any>();

  constructor(
    private worker: CubiqWorker,
    protected factory: ServiceMessageBrokerFactory,
    private detailFillAdapter: DetailFillOrderAdapter,
    private ccyPairLookupService: CcyPairLookupService,
    private positionBookService: PositionBookLookupService,
    private detailedOrderService: DetailedOrderStatusRequestPrimaryService,
    private sysConfigService: SystemConfigPromiseService,
    private notifService: NotificationService,
    private benchmarkLookup: BenchmarkDataLookupService,
    private scratchpadLookup: ScratchPadDataLookupService,
    private rateCache: RateCache,
    private activeUserService: ActiveUserProfilePromiseService,
    private orderLookup: OrderDataLookupService,
  ) {
    super();
    this.broker = this.factory.createMessageBroker(CHANNEL_ORDER_DATA_ACTION_SERVICE, true, true);
    this.NEW_ORDER_REQUEST = CubiqTS.ENCODABLE(CubiqTS.LinkedOrderRequestImpl);
    this.MODIFY_ORDER_REQUEST = CubiqTS.ENCODABLE(CubiqTS.LinkedOrderModificationRequestImpl);
    this.MODIFY_INFO_REQUEST = CubiqTS.ENCODABLE(CubiqTS.LinkedOrderInfoModificationRequestImpl);
    this.MODIFY_RESPONSE = CubiqTS.ENCODABLE(CubiqTS.StrategyModificationLockResponseImpl);
    this.AMEND_MARKUP_LOCK_RESPONSE = CubiqTS.ENCODABLE(
      CubiqTS.StrategyAmendModificationLockResponseImpl,
    );
    this.AMEND_MARKUP_APPROVAL_REQUEST = CubiqTS.ENCODABLE(
      CubiqTS.AmendMarkupSalesApprovalRequestImpl,
    );

    this.PAUSE_RESUME_REQUEST = CubiqTS.ENCODABLE(CubiqTS.OrderPauseResumeRequestImpl);
    this.PAUSE_RESUME_RESPONSE = CubiqTS.ENCODABLE(CubiqTS.OrderPauseResumeResponseImpl);

    this.NEW_BMARK_REQUEST = CubiqTS.ENCODABLE(CubiqTS.FixingOrderRequestImpl);
    this.MODIFY_BMARK_REQUEST = CubiqTS.ENCODABLE(CubiqTS.FixingOrderModificationRequestImpl);

    this.sysConfigService.getSystemConfig(SysFeatures.detailFillBatchCount).then((values) => {
      if (values != undefined) {
        if (values == "0") {
          this.detailFillBatchCount = undefined;
        } else {
          this.detailFillBatchCount = Number(values);
        }
      }
    });
    this.sysConfigService.getSystemConfig(SysFeatures.cancelBatchCount).then((values) => {
      if (values != undefined) {
        if (values == "0") {
          this.cancelBatchCount = undefined;
        } else {
          this.cancelBatchCount = Number(values);
        }
      }
    });

    this.sysConfigService.getSystemConfig(SysFeatures.cancelBurstCount).then((values) => {
      if (values != undefined) {
        this.maxCancelBurstCount = Number(values);
      }

      if (this.maxCancelBurstCount <= 0) {
        logger.out.warn(
          `Adjusting maxCancelBurstCount to 1 as value of ${this.maxCancelBurstCount} is unsupported`,
        );
        this.maxCancelBurstCount = 1;
      }
      logger.out.info(
        `Number of simultaneous cancel Requests (maxCancelBurstCount) = ${this.maxCancelBurstCount}`,
      );
    });

    this.broker.registerMethod(
      "quickFillOrders",
      [PRIMITIVE, PRIMITIVE],
      this.quickFillOrders.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "cancelOrders",
      [PRIMITIVE, PRIMITIVE],
      this.cancelOrders.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "reactivateOrders",
      [PRIMITIVE],
      this.reactivateOrders.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "submitNewOrder",
      [this.NEW_ORDER_REQUEST],
      this.submitNewOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "initModelUpdateService",
      [PRIMITIVE],
      this.initModelUpdateService.bind(this),
      null,
      false,
    );
    this.broker.registerMethod(
      "modifyOrderLock",
      [PRIMITIVE],
      this.modifyOrderLock.bind(this),
      this.MODIFY_RESPONSE,
    );
    this.broker.registerMethod(
      "modifyOrdersUnlock",
      [PRIMITIVE],
      this.modifyOrdersUnlock.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "unlockBenchmarks",
      [PRIMITIVE],
      this.unlockBenchmarks.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "submitModifiedOrder",
      [this.MODIFY_ORDER_REQUEST],
      this.submitModifiedOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "submitModifyInfo",
      [this.MODIFY_INFO_REQUEST],
      this.submitModifyInfo.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "lockOrdersForFill",
      [CubiqTS.ENCODABLE(CubiqTS.OrderDataImpl)],
      this.lockOrdersForFill.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "unlockOrdersFromFill",
      [PRIMITIVE],
      this.unlockOrdersFromFill.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "detailFillOrders",
      [PRIMITIVE],
      this.detailFillOrders.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "releaseOrder",
      [PRIMITIVE, PRIMITIVE],
      this.releaseOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "amendMarkupOrderLock",
      [PRIMITIVE],
      this.amendMarkupOrderLock.bind(this),
      this.AMEND_MARKUP_LOCK_RESPONSE,
    );
    this.broker.registerMethod(
      "amendMarkupApproveAction",
      [this.AMEND_MARKUP_APPROVAL_REQUEST],
      this.amendMarkupApproveAction.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "pauseResumeOrders",
      [this.PAUSE_RESUME_REQUEST],
      this.pauseResumeOrders.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "submitBmarkOrder",
      [this.NEW_BMARK_REQUEST],
      this.submitBmarkOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "submitBmarkOrderModify",
      [this.MODIFY_BMARK_REQUEST],
      this.submitBmarkOrderModify.bind(this),
      PRIMITIVE,
    );
  }

  quickFillOrders(
    orderIds: Array<string>,
    stpRequirement: CubiqTS.FillStpRequirement,
  ): Promise<any> {
    let request: CubiqTS.OrderFillRequestImpl = new CubiqTS.OrderFillRequestImpl();
    request.orderIds = orderIds;
    request.positionBook = null;
    request.fillRequestType = CubiqTS.FillRequestType.QuickFill;
    request.fillRequestData = null;
    request.stpRequirement = stpRequirement;

    return new Promise<any>((resolve, reject) => {
      this.worker.postServiceRequest(request).then((response) => {
        this.notifService.handleErrors(
          response,
          BFXNotificationType.ORDER_ACTION_FAIL,
          BFXNotificationSubType.QUICK_FILL_FAIL,
        );
        resolve(response);
      });
    });
  }

  cancelOrders(o: Map<string, string[]>, reason: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let limitOrderIds = o.get("limitOrderIds");
      let fixingOrderIds = o.get("fixingOrderIds");

      Promise.all([
        this.cancelLimitOrders(limitOrderIds, reason),
        this.cancelFixingOrders(fixingOrderIds, reason),
      ]).then((values) => {
        resolve([...values[0], ...values[1]]);
      });
    });
  }

  cancelLimitOrders(orderIds: string[], reason): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const chunks: any[] = [];
      let chunkSize: number = this.cancelBatchCount ? this.cancelBatchCount : orderIds.length;
      logger.out.info("OrderDataActionPrimaryService:cancelOrders");

      while (orderIds.length) {
        chunks.push(orderIds.splice(0, chunkSize));
      }

      const callables: (() => Promise<any>)[] = [];
      while (chunks.length) {
        callables.push(this.batchCancelLimitOrders.bind(this, chunks.shift(), reason));
      }

      return this.batchExecute(callables, this.maxCancelBurstCount).then((values) =>
        resolve(values),
      );
    });
  }

  /**
   * Convert the array of calls into a stream of invocations that each return a promise. For each member of the stream,
   * execute and wait for promise to resolve, then add the result to an array.
   * Finally, sort the array so that all the results are in the same order as they were in the input.
   * @param callables
   * @param maxConcurrent
   * @returns
   */
  private batchExecute(callables: (() => Promise<any>)[], maxConcurrent: number): Promise<any[]> {
    logger.out.info(
      `batchExecute: sending out ${callables.length} cancel requests - burst count=${maxConcurrent}`,
    );
    const serverCalls = of(...callables);

    const execStream = serverCalls.pipe(
      mergeMap(
        (callable, index) =>
          callable().then((result) => {
            logger.out.info(`Got batch response for request ${index}`);
            // Markup the result with its position so it can be sorted later
            return { result, index };
          }),
        maxConcurrent,
      ),
      toArray(),
      // Sort into initial order and just take the results
      map((tuples) => tuples.sort((v1, v2) => v1.index - v2.index).map(({ result }) => result)),
    );
    return firstValueFrom(execStream);
  }

  cancelFixingOrders(orderIds: string[], reason): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const chunks: any[] = [];
      let chunkSize: number = this.cancelBatchCount ? this.cancelBatchCount : orderIds.length;
      logger.out.info("OrderDataActionPrimaryService:cancelOrders");

      while (orderIds.length) {
        chunks.push(orderIds.splice(0, chunkSize));
      }

      const callables: (() => Promise<any>)[] = [];
      while (chunks.length) {
        callables.push(this.batchCancelFixingOrders.bind(this, chunks.shift(), reason));
      }

      return this.batchExecute(callables, this.maxCancelBurstCount).then((values) =>
        resolve(values),
      );
    });
  }

  private batchCancelLimitOrders(orderIds: Array<string>, reason: string): Promise<any> {
    logger.out.info("batchCancel");
    let request: CubiqTS.OrderCancellationRequestImpl = new CubiqTS.OrderCancellationRequestImpl();
    request.tradingChannel = TRADING_SOURCE_INTERNAL;
    request.orderIds = orderIds;
    request.reason = reason;
    return this.worker.postServiceRequest(request);
  }

  private batchCancelFixingOrders(orderIds: Array<string>, reason: string): Promise<any> {
    logger.out.info("batchCancel");
    let request: CubiqTS.FixingOrderCancellationRequestImpl =
      new CubiqTS.FixingOrderCancellationRequestImpl();
    request.tradingChannel = TRADING_SOURCE_INTERNAL;
    request.orderIds = orderIds;
    request.reason = reason;
    return this.worker.postServiceRequest(request);
  }

  /**
   * Reactivate orders call to server. The orders will be checked if they pass
   * reactivation eligibility using filterOrdersReactivate
   * @param orderIds the order ids for the orders to reactivate
   * @param fromDetailFill indicates if the call comes from detail fill, in this case order locked for fill will be considered eligable for reactivation
   * @returns a promise with the server response
   */
  reactivateOrders(orderIds: Array<string>, fromDetailFill: boolean = false): Promise<any> {
    let request: CubiqTS.OrderReactivationRequestImpl = new CubiqTS.OrderReactivationRequestImpl();

    return new Promise<any>((resolve, reject) => {
      this.filterOrdersReactivate(orderIds, fromDetailFill).then((orderIDs: Array<string>) => {
        logger.out.info(this.constructor.name + ": Reactivating orders [" + orderIDs + "] ");

        request.orderIds = orderIDs;

        this.worker.postServiceRequest(request).then((response) => {
          this.notifService.handleErrors(
            response,
            BFXNotificationType.ORDER_ACTION_FAIL,
            BFXNotificationSubType.REACTIVATE_FAIL,
          );
          resolve(response);
        });
      });
    });
  }

  /**
   * Reactivate action is optimistic on UI but pesimistic on server side, this method will do validation
   * on orders elegible for reactivation and filter them resulting in a list of order ids for orders that are.
   * @param orderIds the order ids for the orders to be checked for reactivation
   * @param fromDetailFill a flag that indicates that the call is coming from detail fill and lock for fill are elegible
   * @param notifyOnFail post a fail notification for the orders that fail the validation
   * @returns a list of order ids elegible for activation
   */
  filterOrdersReactivate(
    orderIds: Array<string>,
    fromDetailFill: boolean = false,
    notifyOnFail: boolean = true,
  ): Promise<Array<string>> {
    return new Promise<Array<string>>((resolve) => {
      this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
        let failedValidationOrdersIds: Array<string> = [];
        orderIds = orderIds == null ? [] : orderIds;
        let orderDataIds: Array<string> = this.orderLookup
          .getOrderDataForIds(orderIds)
          .filter((orderData: CubiqTS.OrderDataImpl) => {
            try {
              let deskId: string = null;

              if (CubiqTS.Long.isLong(orderData.deskId)) {
                deskId = orderData.deskId.toString();
              } else if (typeof orderData.deskId === "string") {
                deskId = orderData.deskId;
              }

              let salesGroupId: string = null;

              if (CubiqTS.Long.isLong(orderData.salesGroupId)) {
                salesGroupId = orderData.salesGroupId.toString();
              } else {
                salesGroupId = `${orderData.salesGroupId}`;
              }

              if (
                orderData &&
                orderData.requestedOrderType != CubiqTS.RequestOrderType.MARKET &&
                (CubiqTS.OrderState.MANUAL_PRICING == orderData.orderState ||
                  CubiqTS.OrderState.TRIGGERED == orderData.orderState ||
                  CubiqTS.OrderState.FILLING == orderData.orderState) &&
                (fromDetailFill
                  ? orderData.lockType === undefined ||
                    orderData.lockType === CubiqTS.LockType.FILL_LOCK ||
                    orderData.lockType === CubiqTS.LockType.FILL_NOW_LOCK
                  : orderData.lockType === undefined)
              ) {
                if (
                  activeUser.userTypeId === CubiqTS.UserType.ADMIN ||
                  (orderData.deskId != null &&
                    activeUser.userTypeId === CubiqTS.UserType.TRADER &&
                    activeUser.tradingGroupsIdSet.has(deskId)) ||
                  (activeUser.userTypeId === CubiqTS.UserType.SALES &&
                    activeUser.salesGroupsIdSet.has(salesGroupId)) ||
                  (activeUser.userTypeId === CubiqTS.UserType.CLIENT &&
                    activeUser.clientIdSet.has(orderData.clientCode))
                ) {
                  return true;
                }
              }

              failedValidationOrdersIds.push(orderData.orderId.toString());

              return false;
            } catch (e) {
              logger.out.error(
                this.constructor.name +
                  ": Failed to validate order for reactivation [" +
                  orderData.orderId.toString() +
                  "], passing it through",
              );
              return true;
            }
          })
          .map((orderData: CubiqTS.OrderDataImpl) => orderData.orderId.toString());

        if (failedValidationOrdersIds.length > 0) {
          logger.out.info(
            this.constructor.name +
              ": Orders failed reactivate validation [" +
              failedValidationOrdersIds +
              "]",
          );
        }

        if (notifyOnFail && failedValidationOrdersIds.length > 0) {
          let response: any = {
            errorMessages: ["{}"],
            orderIds: failedValidationOrdersIds,
          };
          this.notifService.handleErrors(
            response,
            BFXNotificationType.ORDER_ACTION_FAIL,
            BFXNotificationSubType.REACTIVATE_FAIL,
          );
        }

        resolve(orderDataIds);
      });
    });
  }

  submitNewOrder(request: CubiqTS.LinkedOrderRequestImpl): Promise<any> {
    request.rootOrder.useDefaultClientDataFg = false;
    request.rootOrder.tradingChannel = TRADING_SOURCE_INTERNAL;

    this.scratchpadLookup.processTradingChannel(request);

    //logger.out.info(`HSTP-DEBUG: WORKER OrderDataAction.submitNewOrder(): REQUEST DUMP: %O`, request);
    return this.worker.postServiceRequest(request);
  }

  submitBmarkOrder(request: CubiqTS.FixingOrderRequestImpl): Promise<any> {
    request.fixingOrderData.tradingChannel = TRADING_SOURCE_INTERNAL;
    request.fixingOrderData.useDefaultClientDataFg = false;

    this.scratchpadLookup.processTradingChannel(request);

    if (request.fixingOrderData.orderId != null) {
      let oldReq: CubiqTS.FixingOrderRequestImpl = this.scratchpadLookup.getItem(
        request.fixingOrderData.externalClientReference,
      ) as CubiqTS.FixingOrderRequestImpl;
      if (
        oldReq != null &&
        oldReq.fixingOrderData.allocations != null &&
        oldReq.fixingOrderData.allocations.length > 0
      ) {
        let allocationRequest: CubiqTS.AllocationRequestDataImpl =
          oldReq.fixingOrderData.allocations[0];
        if (
          request.fixingOrderData.allocations == null &&
          allocationRequest.allocationReferenceId != null
        ) {
          let newAllocation: CubiqTS.AllocationRequestDataImpl =
            new CubiqTS.AllocationRequestDataImpl();
          let fixingOrder: CubiqTS.FixingOrderRequestDataImpl =
            request.fixingOrderData as CubiqTS.FixingOrderRequestDataImpl;
          newAllocation.allocationReferenceId = allocationRequest.allocationReferenceId;
          newAllocation.accountCode = fixingOrder.accountCode;
          newAllocation.allocationIntent = fixingOrder.clientIntent;
          newAllocation.allocationQty = fixingOrder.fixedAmount;
          newAllocation.markupQty = fixingOrder.markup;
          newAllocation.markupType = fixingOrder.markupType;
          fixingOrder.allocations = [newAllocation];
        }
      }
    }

    return this.worker.postServiceRequest(request);
  }

  submitBmarkOrderModify(request: CubiqTS.FixingOrderModificationRequest): Promise<any> {
    request.fixingOrderData.tradingChannel = TRADING_SOURCE_INTERNAL;
    return this.worker.postServiceRequest(request);
  }

  submitModifiedOrder(request: CubiqTS.LinkedOrderModificationRequestImpl): Promise<any> {
    request.cancelAndReplace = false;
    request.rootOrder.tradingChannel = TRADING_SOURCE_INTERNAL;
    return this.worker.postServiceRequest(request);
  }

  submitModifyInfo(request: CubiqTS.LinkedOrderInfoModificationRequestImpl): Promise<any> {
    return this.worker.postServiceRequest(request);
  }

  modifyOrderLock(orderId: string): Promise<any> {
    let request: CubiqTS.ModificationLockRequestImpl = new CubiqTS.ModificationLockRequestImpl();
    request.lockType = CubiqTS.LockType.MODIFY_LOCK;
    request.invokingSource = "INTERNAL";
    request.orderId = orderId;

    return new Promise<any>((resolve, reject) => {
      this.worker
        .postServiceRequest(request)
        .then((response: CubiqTS.StrategyModificationLockResponse | CubiqTS.Errors) => {
          /*  this.handleErrors(response, BFXNotificationType.ORDER_ACTION_FAIL ,BFXNotificationSubType.MODIFY_OUT_FAIL);
                  resolve(response); */

          if (response instanceof CubiqTS.ErrorsImpl) {
            // errors
            let realResponse = new CubiqTS.StrategyModificationLockResponseImpl();
            realResponse.lockErrors = response;
            realResponse.extendedInfo.set("orderRef", this.orderLookup.getOrderRef(orderId));
            resolve(realResponse);
          } else if (
            response instanceof CubiqTS.StrategyModificationLockResponseImpl &&
            response.orderData != null
          ) {
            // success
            this.detailedOrderService.preprocessOrderDataResults(response.orderData);
            resolve(response);
          } else if (
            response instanceof CubiqTS.StrategyModificationLockResponseImpl &&
            response.orderData == null &&
            response.lockErrors instanceof CubiqTS.ErrorsImpl
          ) {
            // lock errors
            response.extendedInfo.set("orderRef", this.orderLookup.getOrderRef(orderId));
            resolve(response);
          } else {
            resolve(null);
          }
        });
    });
  }

  modifyOrdersUnlock(orderIds: Array<string>): Promise<any> {
    let request: CubiqTS.UnlockRequestImpl = new CubiqTS.UnlockRequestImpl();
    request.orderIds = orderIds;
    request.lockType = CubiqTS.LockType.MODIFY_LOCK;
    return this.worker.postServiceRequest(request);
    /*  new Promise<any>((resolve, reject) => {
            this.worker.postServiceRequest(request).then((response => {
                this.handleErrors(response, BFXNotificationType.ORDER_ACTION_FAIL ,BFXNotificationSubType.UNLOCK_FAIL);
                resolve(response);
            }));
        }); */
  }

  unlockBenchmarks(orderIds: Array<string>): Promise<any> {
    let request: CubiqTS.UnlockRequestImpl = new CubiqTS.UnlockRequestImpl();
    request.orderIds = orderIds;
    request.lockType = CubiqTS.LockType.MODIFY_LOCK;
    request.orderType = CubiqTS.OrderType.BENCHMARK;
    return new Promise<any>((resolve, reject) => {
      this.worker.postServiceRequest(request).then((response) => {
        this.notifService.handleErrors(
          response,
          BFXNotificationType.ORDER_ACTION_FAIL,
          BFXNotificationSubType.UNLOCK_FAIL,
        );
        resolve(response);
      });
    });
  }

  unlockOrdersFromFill(orderIds: Array<string>): Promise<any> {
    let request: CubiqTS.UnlockRequestImpl = new CubiqTS.UnlockRequestImpl();
    request.orderIds = orderIds;
    request.lockType = CubiqTS.LockType.FILL_LOCK;
    return new Promise<any>((resolve, reject) => {
      this.worker.postServiceRequest(request).then((response) => {
        let handledError = this.notifService.handleErrors(
          response,
          BFXNotificationType.ORDER_ACTION_FAIL,
          BFXNotificationSubType.UNLOCK_FAIL,
        );
        if (!handledError) {
          orderIds.forEach((orderId) => {
            this.rateCache.clearDetailFillPtsLock(orderId);
          });
        }
        resolve(response);
      });
    });
  }

  lockOrdersForFill(orders: Array<CubiqTS.OrderDataImpl>): Promise<DetailFillCcyPair[]> {
    let request: CubiqTS.FillLockRequestImpl = new CubiqTS.FillLockRequestImpl();
    let orderDataMap = new Map();
    const adapted: Array<DetailFillCcyPair> = [];

    return new Promise<any>((resolve, reject) => {
      this.filterOrdersDetailFill(orders).then((orderData: Array<CubiqTS.OrderDataImpl>) => {
        request.orderIds = orderData.map((order) => {
          let orderId = order.orderId.toString();
          orderDataMap.set(orderId, order);
          return orderId;
        });

        logger.out.info(
          this.constructor.name + ": Locking orders for fill [" + request.orderIds + "] ",
        );

        request.lockType = CubiqTS.LockType.FILL_LOCK;
        request.invokingSource = "INTERNAL";

        this.worker.postServiceRequest(request).then(async (response) => {
          let fillData: Array<CubiqTS.OrderFillDataImpl> = response.fillData;

          let pairMap: Map<string, DetailFillCcyPair> = new Map();
          if (Array.isArray(fillData)) {
            for (let orderFillData of fillData) {
              let key: string = this.getPairName(orderFillData);
              if (!pairMap.get(key)) {
                let ccyPair = this.ccyPairLookupService.getCcyPair(
                  key.substr(0, 3),
                  key.substr(3, 3),
                );
                key = ccyPair.base + ccyPair.nonBase;
                let minPrecision = ccyPair.pipsQty;
                let maxPrecision = ccyPair.fwdRateDecimalsQty;
                let newPair: DetailFillCcyPair = {
                  pairName: key,
                  rowData: [],
                  minPrecision: minPrecision,
                  maxPrecision: maxPrecision,
                };
                pairMap.set(key, newPair);
                adapted.push(newPair);
              }

              let row = await this.detailFillAdapter.adaptOrderFill(orderFillData, fillData);
              if (row != null) pairMap.get(key).rowData.push(row);
            }
            resolve(adapted);
          }
          this.notifService.handleErrors(
            response,
            BFXNotificationType.ORDER_ACTION_FAIL,
            BFXNotificationSubType.DETAIL_FILL_FAIL,
          );
        });
      });
    });
  }

  /**
   * Detail fill action is optimistic on UI when invoked from notifications but pesimistic on server side, this method will do validation
   * on orders elegible for detail fill and filter resulting in a list of order ids for orders that are.
   * @param orderData the orders to be checked for detail fill (lock for fill)
   * @param notifyOnFail post a fail notification for the orders that fail the validation
   * @returns a list of orders elegible for activation
   */
  filterOrdersDetailFill(
    orderData: Array<CubiqTS.OrderDataImpl>,
    notifyOnFail: boolean = true,
  ): Promise<Array<CubiqTS.OrderDataImpl>> {
    return new Promise<Array<CubiqTS.OrderDataImpl>>((resolve) => {
      this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
        let failedValidationOrdersIds: Array<string> = [];
        let relatedOrders: any = {};

        let orders: Array<CubiqTS.OrderDataImpl> = orderData.filter(
          (orderData: CubiqTS.OrderDataImpl) => {
            let actionEnabled: boolean = true;
            let relatedOrderId: string = null;

            try {
              // If two siblings of an OCO are selected quick fill should be disabled as per OMS-21024
              if (
                orderData.siblingRelationship != null &&
                orderData.siblingRelationship.relationshipType === CubiqTS.OrderRelationshipType.OCO
              ) {
                relatedOrderId = orderData.siblingRelationship.orderId;

                if (relatedOrders[relatedOrderId] != null) {
                  // A OCO sibling is selected, disable the quick fill
                  actionEnabled = false;
                }
              }

              // Order is locked
              if (
                orderData.lockType !== null &&
                (orderData.lockType === CubiqTS.LockType.FILL_LOCK ||
                  orderData.lockType === CubiqTS.LockType.FILL_NOW_LOCK ||
                  orderData.lockType === CubiqTS.LockType.MODIFY_LOCK ||
                  orderData.lockType === CubiqTS.LockType.SI_LOCK ||
                  orderData.lockType === CubiqTS.LockType.CANCEL_LOCK ||
                  orderData.lockType === CubiqTS.LockType.TRIGGER_LOCK)
              ) {
                actionEnabled = false;
              }

              let deskId: string = null;

              if (CubiqTS.Long.isLong(orderData.deskId)) {
                deskId = orderData.deskId.toString();
              } else {
                deskId = `${orderData.deskId}`;
              }

              let salesGroupId: string = null;

              if (CubiqTS.Long.isLong(orderData.salesGroupId)) {
                salesGroupId = orderData.salesGroupId.toString();
              } else {
                salesGroupId = `${orderData.salesGroupId}`;
              }

              if (
                activeUser.isTrader() &&
                orderData.deskId != null &&
                !activeUser.tradingGroupsIdSet.has(deskId)
              ) {
                actionEnabled = false;
              }

              if (
                activeUser.isSales() &&
                orderData.salesGroupId &&
                !activeUser.salesGroupsIdSet.has(salesGroupId)
              ) {
                actionEnabled = false;
              }

              // Order state:   in intervention, or suspended
              switch (orderData.orderState) {
                case CubiqTS.OrderState.PENDING:
                case CubiqTS.OrderState.PENDING_CANCELLATION:
                // intervention
                case CubiqTS.OrderState.ACCEPTANCE_NEEDED: // Modify in Pending, New Pending
                case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED: // Modify Out Pending
                case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED: // Cancel Pending
                case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED: // Migration Acceptance Needed
                // suspended
                case CubiqTS.OrderState.SUSPENDED: // Suspended
                case CubiqTS.OrderState.FILL_PENDING:
                  actionEnabled = false;
                  break;
              }

              // no detail fill for paused orders
              if (
                CubiqTS.PauseState.PAUSED === orderData.pauseState ||
                CubiqTS.PauseState.PAUSE_PENDING === orderData.pauseState
              ) {
                actionEnabled = false;
              }

              if (actionEnabled && relatedOrderId != null) {
                let orderId: string = orderData.orderId.toString();
                relatedOrders[orderId] = relatedOrderId;
              } else if (!actionEnabled) {
                failedValidationOrdersIds.push(orderData.orderId.toString());
              }

              return actionEnabled;
            } catch (e) {
              logger.out.error(
                this.constructor.name +
                  ": Failed to validate order for detail fill [" +
                  orderData.orderId.toString() +
                  "], passing it through",
              );
              return true;
            }
          },
        );

        if (failedValidationOrdersIds.length > 0) {
          logger.out.info(
            this.constructor.name +
              ": Orders failed detail fill validation [" +
              failedValidationOrdersIds +
              "]",
          );
        }

        if (notifyOnFail && failedValidationOrdersIds.length > 0) {
          let response: any = {
            errorMessages: ["{}"],
            orderIds: failedValidationOrdersIds,
          };
          this.notifService.handleErrors(
            response,
            BFXNotificationType.ORDER_ACTION_FAIL,
            BFXNotificationSubType.DETAIL_FILL_FAIL,
          );
        }

        resolve(orders);
      });
    });
  }

  detailFillOrders(fillOrders: Array<FillOrderData>): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const chunks: any[] = [];
      let chunkSize: number = this.detailFillBatchCount
        ? this.detailFillBatchCount
        : fillOrders.length;
      logger.out.info("OrderDataActionPrimaryService:detailFillOrders");
      while (fillOrders.length) {
        chunks.push(fillOrders.splice(0, chunkSize));
      }
      let chunkCount = chunks.length;
      let chunkResponse = 0;
      let responses: OrderFillResultImpl[] = [];
      while (chunks.length) {
        this.batchDetailFillOrders(chunks.shift()).then((response) => {
          logger.out.info("batchDetailFillOrders response");
          chunkResponse++;
          response.results.forEach((result) => {
            responses.push(result);
          });
          if (chunkResponse == chunkCount) {
            logger.out.info("batchDetailFillOrders complete");
            resolve(responses);
          }
        });
      }
    });
  }

  batchDetailFillOrders(fillOrders: Array<FillOrderData>): Promise<any> {
    let request: CubiqTS.OrderFillRequestImpl = new CubiqTS.OrderFillRequestImpl();
    request.positionBook = null;
    request.fillRequestType = CubiqTS.FillRequestType.DetailFill;
    request.fillRequestData = [];
    fillOrders.forEach((fillOrder) => {
      let ofd: CubiqTS.OrderFillDataImpl = new CubiqTS.OrderFillDataImpl();
      ofd.orderId = fillOrder.orderId;
      if (fillOrder.fillAmt) ofd.fillAmount = new CubiqTS.BigNumber(fillOrder.fillAmt);
      ofd.stpType = fillOrder.stp ? CubiqTS.FillStpType.STP_ALL : CubiqTS.FillStpType.STP_NONE;
      ofd.stpHold = fillOrder.stpHold;
      if (fillOrder.valueDate != null) ofd.valueDate = fillOrder.valueDate;
      ofd.fillData = new CubiqTS.RateDataImpl();
      ofd.fillData.baseCcyCd = fillOrder.baseCcy;
      ofd.fillData.nonbaseCcyCd = fillOrder.nonBaseCcy;
      ofd.fillData.isMarketConvention = !fillOrder.isInvert;
      if (fillOrder.crosses && fillOrder.crosses.length > 0) {
        let leg1 = this.createCrossLeg(fillOrder.crosses[0]);
        let leg2 = this.createCrossLeg(fillOrder.crosses[1]);
        ofd.fillData.componentRateData = [leg1, leg2];
      } else {
        ofd.fillData.book = this.positionBookService.getPositionBook(fillOrder.positionBookId);
        // if the book is not visible to me (client is not visible), create a dummy one
        if (!ofd.fillData.book && fillOrder.positionBookId) {
          ofd.fillData.book = new CubiqTS.PositionBookImpl();
          ofd.fillData.book.positionBookId = new CubiqTS.Long(Number(fillOrder.positionBookId));
        }
      }
      if (fillOrder.mktSpot != null) {
        ofd.fillData.spotMarketRate = new CubiqTS.BigNumber(fillOrder.mktSpot);
        ofd.fillData.forwardMarketPoints = new CubiqTS.BigNumber(fillOrder.mktPoints);
        ofd.fillData.marketAllIn = new CubiqTS.BigNumber(fillOrder.mktAllIn);
      }
      if (fillOrder.clientSpot != null) {
        ofd.fillData.spotClientRate = new CubiqTS.BigNumber(fillOrder.clientSpot);
        ofd.fillData.forwardClientPoints = new CubiqTS.BigNumber(fillOrder.clientPoints);
        ofd.fillData.clientAllIn = new CubiqTS.BigNumber(fillOrder.clientAllIn);
      }
      // componentRateData
      request.fillRequestData.push(ofd);
    });

    return new Promise<any>((resolve, reject) => {
      this.worker.postServiceRequest(request).then(
        (response) => {
          this.notifService.handleErrors(
            response,
            BFXNotificationType.ORDER_ACTION_FAIL,
            BFXNotificationSubType.DETAIL_FILL_FAIL,
          );
          resolve(response);

          // if(!this.handleErrors(response, BFXNotificationType.ORDER_ACTION_FAIL, BFXNotificationSubType.DETAIL_FILL_FAIL)){
          //     if (response && response.errorMessages&&response.errorMessages.length>0) {
          //         // let error: any =JSON.parse(response.errorMessages[0]);
          //         this.fireModelNotificationFailureEvent(response, {notifType:BFXNotificationType.ORDER_ACTION_FAIL,notifSubType:BFXNotificationSubType.DETAIL_FILL_FAIL});
          //         reject(response);
          //     } else{
          //         resolve([]);
          //     }
          // } else {
          //     resolve(idsToUnlock);
          // }
        },
        (err) => {
          logger.out.error("ERROR: BfxBlotterOrdersActionService.detailFillOrders failed!");
          reject(err);
        },
      );
    });
  }

  private createCrossLeg(leg: FillOrderData): CubiqTS.RateDataImpl {
    let legData = new CubiqTS.RateDataImpl();
    legData.baseCcyCd = leg.baseCcy;
    legData.nonbaseCcyCd = leg.nonBaseCcy;
    legData.isMarketConvention = true;
    legData.book = this.positionBookService.getPositionBook(leg.positionBookId);
    // if the book is not visible to me (client is not visible), create a dummy one
    if (!legData.book && leg.positionBookId) {
      legData.book = new CubiqTS.PositionBookImpl();
      legData.book.positionBookId = new CubiqTS.Long(Number(leg.positionBookId));
    }
    if (leg.mktSpot != null) {
      legData.spotMarketRate = new CubiqTS.BigNumber(leg.mktSpot);
      //spot only at the moment, but server seems to require the all in also
      legData.marketAllIn = new CubiqTS.BigNumber(leg.mktSpot);
      // set the same as the market rate for cross legs
      legData.spotClientRate = new CubiqTS.BigNumber(leg.mktSpot);
      //spot only at the moment, but server seems to require the all in also
      legData.clientAllIn = new CubiqTS.BigNumber(leg.mktSpot);
    }

    return legData;
  }

  public releaseOrder(ids: Array<string>, orderType: CubiqTS.OrderType): Promise<void> {
    let request: CubiqTS.UnlockRequestImpl = new CubiqTS.UnlockRequestImpl();
    request.orderIds = ids;
    request.lockType = CubiqTS.LockType.SI_LOCK;
    if (orderType != null) {
      request.orderType = orderType;
    }
    return this.worker.postServiceRequest(request).then((response) => {
      this.notifService.handleErrors(
        response,
        BFXNotificationType.ORDER_ACTION_FAIL,
        BFXNotificationSubType.UNLOCK_FAIL,
      );
    });
  }

  private getPairName(fillData: CubiqTS.OrderFillDataImpl): string {
    if (fillData && fillData.fillData) {
      if (fillData.fillData.isMarketConvention)
        return fillData.fillData.baseCcyCd + fillData.fillData.nonbaseCcyCd;
      return fillData.fillData.nonbaseCcyCd + fillData.fillData.baseCcyCd;
    }
    return "ALL";
  }

  getModelNotificationFailureEventSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.modelNotificationFailureEventSubject;
  }

  amendMarkupOrderLock(orderId: string): Promise<any> {
    let request: CubiqTS.AmendMarkupLockRequestImpl = new CubiqTS.AmendMarkupLockRequestImpl();
    request.orderId = orderId;
    return new Promise<any>((resolve, reject) => {
      this.worker
        .postServiceRequest(request)
        .then((response: CubiqTS.StrategyAmendModificationLockResponse | CubiqTS.Errors) => {
          if (response instanceof CubiqTS.ErrorsImpl) {
            let realResponse = new CubiqTS.StrategyAmendModificationLockResponseImpl();
            realResponse.lockErrors = response;
            resolve(realResponse);
          } else {
            resolve(response);
          }
        });
    });
  }

  amendMarkupApproveAction(request: CubiqTS.AmendMarkupSalesApprovalRequestImpl): Promise<any> {
    return this.worker.postServiceRequest(request);
  }

  pauseResumeOrders(request: CubiqTS.OrderPauseResumeRequestImpl): Promise<any> {
    return this.worker.postServiceRequest(request);
  }

  /**
   * Export orders for import
   * @param orderIds the order IDs of the orders to be exported
   * @param exportMarkup should the markup be in the export
   */
  exportOrders(
    orderIds: string[],
    exportMarkup: boolean,
  ): Promise<CubiqTS.OrderExportResponseImpl> {
    let request: CubiqTS.OrderExportRequestImpl = new CubiqTS.OrderExportRequestImpl();
    request.orderIds = orderIds;
    request.includeMarkup = exportMarkup;

    // TODO Map export markup when ported to server side

    return new Promise<CubiqTS.OrderExportResponseImpl>((resolve, reject) => {
      this.worker
        .postServiceRequest(request)
        .then((value: CubiqTS.OrderExportRequestImpl | CubiqTS.ErrorsImpl) => {
          if (value instanceof CubiqTS.OrderExportResponseImpl) {
            resolve(value);
          }

          if (value instanceof CubiqTS.ErrorsImpl) {
            reject(value);
          }
        });
    });
  }

  /**
   * Sends a toggle inflight inclusion state request to server to set the inflight inclusion
   * state for a set of orders
   * @param orderIds the order ids of the orders to set the inflight inclusion state
   * @param state the inflight inclusion state
   * @returns a promise that will be resolved on response or rejected if and Errors object is returned
   */
  toggleInflightInclusionState(
    orderIds: CubiqTS.Long[],
    state: CubiqTS.InflightInclusionState,
  ): Promise<CubiqTS.ToggleInflightStateResult> {
    let request: CubiqTS.ToggleInflightStateRequestImpl =
      new CubiqTS.ToggleInflightStateRequestImpl();
    request.orderIds = orderIds;
    request.inflightInclusionState = state;
    let isEnable: boolean = state === CubiqTS.InflightInclusionState.ENABLED;

    return new Promise<CubiqTS.ToggleInflightStateResultImpl>((res, rej) => {
      this.worker.postServiceRequest(request).then((value) => {
        if (value instanceof CubiqTS.ErrorsImpl) {
          this.notifService.handleErrors(
            value,
            BFXNotificationType.ORDER_ACTION_FAIL,
            BFXNotificationSubType.QUICK_FILL_FAIL,
          );
          rej(value);
        }

        if (value instanceof CubiqTS.ToggleInflightStateResultImpl) {
          let toggleResponse: CubiqTS.ToggleInflightStateResultImpl = value;
          let time: Date = new Date();
          let notifData: INotificationEvent[] = toggleResponse.results
            .filter((r) => !r.success)
            .map((value) =>
              this.notifService.inflightChangeResponseToNotification(value, isEnable, time),
            );
          notifData.forEach((e) =>
            this.notifService.fireNotificationEvent(
              e,
              BFXNotificationEventType.ALGO_INFLIGHT_FAILED,
            ),
          );
        }

        res(value);
      });
    });
  }

  /**
   * Send a FillNowLock request for the specified order to the server
   * @param orderId
   */
  public fillNowLock(orderId: string): Promise<CubiqTS.FillNowLockResponse | CubiqTS.Errors> {
    let request: CubiqTS.FillNowLockRequest = new CubiqTS.FillNowLockRequestImpl();
    request.orderId = orderId;
    request.lockType = CubiqTS.LockType.FILL_NOW_LOCK;

    return new Promise((res, rej) => {
      this.worker
        .postServiceRequest(request)
        .then((value: CubiqTS.FillNowLockResponse | CubiqTS.ErrorsImpl) => {
          if (value instanceof CubiqTS.ErrorsImpl) {
            this.notifService.handleErrors(
              value,
              BFXNotificationType.ORDER_ACTION_FAIL,
              BFXNotificationSubType.FILL_NOW_LOCK_FAIL,
            );
            rej(value);
          }
          if (value instanceof CubiqTS.FillNowLockResponseImpl) {
            res(value);
          } else {
            const unexpected = new CubiqTS.ErrorsImpl();
            unexpected.errorMessages = ['{"id":"unknown.response}'] as any[];
            rej(unexpected);
          }
        });
    });
  }

  /**
   * Should user not wish to process with fillNow, send a general unlock
   * @param orderId
   */
  public fillNowUnlock(orderIds: string[]): Promise<any> {
    let request: CubiqTS.UnlockRequestImpl = new CubiqTS.UnlockRequestImpl();
    request.orderIds = orderIds;
    request.lockType = CubiqTS.LockType.FILL_NOW_LOCK;
    return this.worker.postServiceRequest(request);
  }

  /**
   * Create a fill now request forr the specified order Id
   * @param orderId
   * @param fillAmount Amount to set in the order request - if null, then the entire reamining samount is filled
   */
  public fillNow(
    orderId: string,
    fillAmount: CubiqTS.BigNumber,
  ): Promise<CubiqTS.OrderFillNowResponse> {
    let request: CubiqTS.OrderFillNowRequest = new CubiqTS.OrderFillNowRequestImpl();
    request.orderId = orderId;
    if (fillAmount) {
      request.fillNowAmt = fillAmount;
      request.useFullOpenAmountFg = false;
    } else {
      request.useFullOpenAmountFg = true;
    }

    return new Promise((res, rej) => {
      this.worker
        .postServiceRequest(request)
        .then((value: CubiqTS.OrderFillNowResponse | CubiqTS.ErrorsImpl) => {
          if (value instanceof CubiqTS.ErrorsImpl) {
            this.notifService.handleErrors(
              value,
              BFXNotificationType.ORDER_ACTION_FAIL,
              BFXNotificationSubType.FILL_NOW_FAIL,
            );
            rej(value);
          }
          if (value instanceof CubiqTS.OrderFillNowResponseImpl) {
            res(value);
          } else {
            const unexpected = new CubiqTS.ErrorsImpl();
            unexpected.errorMessages = ['{"id":"unknown.fillnow.response}'] as any[];
            rej(unexpected);
          }
        });
    });
  }

  /**
   * execute a netting operation on the server on specified orders
   * @param netOperation Net/Clear Netting/Disable Netting operation
   * @param orderIds
   */
  public netBenchmarkOrders(netOperation: ManualNettingAction, orderIds: string[]): Promise<any> {
    const request: ManualNettingRequestImpl = new ManualNettingRequestImpl();
    request.action = netOperation;
    request.orderIds = orderIds.map((orderId) => CubiqTS.Long.fromString(orderId));
    return new Promise((res, rej) => {
      this.worker
        .postServiceRequest(request)
        .then((value: ManualNettingResponseImpl | CubiqTS.ErrorsImpl) => {
          if (value instanceof CubiqTS.ErrorsImpl) {
            rej(value);
          } else {
            res(value);
          }
        });
    });
  }
}
